import { type FC, lazy } from 'react'
import { useTranslation } from 'react-i18next'
import { createBrowserRouter } from 'react-router-dom'
import { AppNavigationContext } from '#app/navigation/AppNavigationContext'
import { AppProvider } from '#app/routes/AppProvider'
import { FullscreenLoading } from '#components/FullScreenLoading'
import { PageRouteOutlet } from '#features/AppLayout/PageRouteOutlet'
import { DebugSettingsPopup } from '#features/DebugSettings/DebugSettingsPopup'
import { ErrorDisplayPage } from '#features/ErrorHandling/ErrorDisplay'
import { FullPageErrorBoundary } from '#features/ErrorHandling/FullPageErrorBoundary'
import { AppHeader } from '#features/Header/AppHeader'
import { HomeHeader } from '#features/Header/HomeHeader'
import { ManCodeOverlayProvider } from '#features/ManCode/ManCodeInformationOverlay'
import { AuthenticatedRoutes } from '#features/User/AuthenticatedRoutes'
import { buildAuthRouteFragments } from '#features/User/buildAuthRouteFragments'
import { ChangeBaseParametersOverlayProvider } from '#pages/Configuration/ChangeBaseParameters/ChangeBaseParametersContext'
import { EntryRouting } from './EntryRouting'

const Landing = lazy(() => import('../../pages/Landing').then((module) => ({ default: module.Landing })))
const ReferenceTruck = lazy(() => import('../../pages/ReferenceTruck').then((module) => ({ default: module.ReferenceTruck })))
const Configuration = lazy(() => import('#pages/ConfigurationRootPage.tsx').then((module) => ({ default: module.ConfigurationRootPage })))
const TechnicalSetup = lazy(() => import('../../pages/TechnicalSetup').then((module) => ({ default: module.TechnicalSetup })))
const Bridge = lazy(() => import('../../pages/Bridge').then((module) => ({ default: module.Bridge })))

// biome-ignore lint/style/useComponentExportOnlyModules: does not need to be exported
const FrontendAppLayout: FC = () => {
  const { t } = useTranslation()

  return (
    <AppProvider>
      <AppNavigationContext>
        <ManCodeOverlayProvider>
          <ChangeBaseParametersOverlayProvider>
            <PageRouteOutlet appName={t('app.name', 'MAN Intelligent Configurator')} />
            <DebugSettingsPopup />
          </ChangeBaseParametersOverlayProvider>
        </ManCodeOverlayProvider>
      </AppNavigationContext>
    </AppProvider>
  )
}

// biome-ignore lint/style/useComponentExportOnlyModules: does not need to be exported
const AppErrorBoundary: FC = () => {
  return (
    <>
      <AppHeader />
      <ErrorDisplayPage />
    </>
  )
}

// biome-ignore lint/style/useComponentExportOnlyModules: does not need to be exported
const AppErrorDisplay: FC = () => {
  const { t } = useTranslation()
  return (
    <AppProvider>
      <FullPageErrorBoundary appName={t('app.name', 'MAN Intelligent Configurator')} headerContent={<HomeHeader />} />
      <DebugSettingsPopup />
    </AppProvider>
  )
}

export const router = createBrowserRouter([
  {
    ErrorBoundary: AppErrorDisplay,
    element: <FrontendAppLayout />,
    hydrateFallbackElement: <FullscreenLoading />,
    children: [
      ...buildAuthRouteFragments(<AppHeader />),
      {
        element: <AuthenticatedRoutes pageHeader={<AppHeader />} />,
        children: [
          {
            index: true,
            element: <EntryRouting />,
            ErrorBoundary: AppErrorBoundary,
          },
          {
            path: '/setup',
            element: <TechnicalSetup />,
          },
          {
            path: '/landing',
            element: <Landing />,
            ErrorBoundary: AppErrorBoundary,
          },
          {
            path: '/reference-truck',
            element: <ReferenceTruck />,
            ErrorBoundary: AppErrorBoundary,
          },
          {
            path: '/configuration/*',
            element: <Configuration />,
            ErrorBoundary: AppErrorBoundary,
          },
        ],
      },
      {
        path: '/bridge',
        element: <Bridge />,
        ErrorBoundary: AppErrorBoundary,
      },
    ],
  },
])
